import { Input } from "antd";
import type { InputProps, InputRef } from "antd";
import { useEffect, useRef, useState } from "react";
import type { ClipboardEvent, CompositionEvent, DragEvent } from "react";
import type { InputPolicy } from "./inputPolicy";
import { candidateOf, isBulk, judge, numberDisplay, numberValue } from "./inputPolicy";
import "./actionPanel.css";

type Props = Omit<InputProps, "value" | "onChange"> & {
  policy: InputPolicy;
  value?: unknown;
  /** A number field gets the plain form of the API ("1.5"), a text field the text as typed. */
  onChange?: (value: string | undefined) => void;
};

/**
 * UXW-61…63 (option «Запрет недопустимого ввода»): an input that judges the whole value of every edit by its policy.
 * A refused edit never reaches the form — the previous value, the caret and the undo history stay — and the reason
 * is said next to the field in a polite live region (no toast per key). Keys that do not insert anything (Backspace,
 * Delete, arrows, Ctrl/Cmd+A/C/V/Z) are never blocked; an IME composition is judged when it ends.
 */
export function GuardedInput({ policy, value, onChange, ...rest }: Props) {
  const numeric = policy.kind === "number";
  const [text, setText] = useState(() => (numeric ? numberDisplay(value) : ""));
  const [reason, setReason] = useState<string | null>(null);
  const emitted = useRef<unknown>(value);
  const composing = useRef(false);
  const beforeComposition = useRef("");
  const input = useRef<InputRef>(null);
  const policyRef = useRef(policy);
  policyRef.current = policy;

  // a value set by the form (loaded, saved, computed from packages) is shown as it is, with a comma
  useEffect(() => {
    if (!numeric || value === emitted.current) return;
    emitted.current = value;
    const typed = numberValue(text);
    if (typed !== undefined && value !== undefined && value !== null && value !== "" && Number(typed) === Number(value)) return;
    setText(numberDisplay(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, numeric]);

  const shown = numeric ? text : value === undefined || value === null ? "" : String(value);

  const accept = (next: string) => {
    setReason(null);
    if (numeric) {
      setText(next);
      const plain = numberValue(next);
      emitted.current = plain;
      onChange?.(plain);
    } else onChange?.(next);
  };

  /** The keys and the insert of the browser: refused before they touch the field, so the caret does not jump. */
  useEffect(() => {
    const el = input.current?.input;
    if (!el) return;
    const before = (e: InputEvent) => {
      if (e.isComposing || composing.current) return;
      const type = e.inputType ?? "";
      // a paste and a drop have their own events (their text is known there); deletions are never blocked
      if (!type.startsWith("insert") || type === "insertCompositionText" || type === "insertFromPaste" || type === "insertFromDrop") return;
      const data = e.data ?? "";
      const verdict = judge(policyRef.current, candidateOf(el.value, el.selectionStart, el.selectionEnd, data), { paste: isBulk(type, data) });
      if (!verdict.ok) {
        e.preventDefault();
        setReason(verdict.reason);
      }
    };
    el.addEventListener("beforeinput", before);
    return () => el.removeEventListener("beforeinput", before);
  }, []);

  /** A paste or a drop is judged whole (IN02, IN07): refused — nothing of it is inserted, nothing is cut. */
  const judgeInsert = (el: HTMLInputElement, inserted: string, prevent: () => void) => {
    const verdict = judge(policyRef.current, candidateOf(el.value, el.selectionStart, el.selectionEnd, inserted), { paste: true });
    if (!verdict.ok) {
      prevent();
      setReason(verdict.reason);
    }
  };

  return (
    <>
      <Input
        ref={input}
        inputMode={policy.kind === "number" ? (policy.integer || policy.decimals === 0 ? "numeric" : "decimal") : policy.kind === "tax_id" || policy.kind === "kbe" ? "numeric" : policy.kind === "phone" ? "tel" : undefined}
        title={!numeric && shown.length > 16 ? shown : undefined}
        {...rest}
        value={shown}
        onPaste={(e: ClipboardEvent<HTMLInputElement>) => judgeInsert(e.currentTarget, e.clipboardData.getData("text"), () => e.preventDefault())}
        onDrop={(e: DragEvent<HTMLInputElement>) => judgeInsert(e.currentTarget, e.dataTransfer.getData("text"), () => e.preventDefault())}
        onCompositionStart={() => {
          composing.current = true;
          beforeComposition.current = shown;
        }}
        onCompositionEnd={(e: CompositionEvent<HTMLInputElement>) => {
          composing.current = false;
          // IN04: the finished composition is judged once; a refused one returns the value it started from
          const verdict = judge(policyRef.current, e.currentTarget.value);
          if (verdict.ok) accept(e.currentTarget.value);
          else {
            accept(beforeComposition.current);
            setReason(verdict.reason);
          }
        }}
        onChange={(e) => {
          const next = e.target.value;
          if (composing.current) {
            // the composition is not filtered on its way (Kazakh and Russian keyboards, dictation)
            if (numeric) setText(next);
            else onChange?.(next);
            return;
          }
          // a deletion is never blocked — also inside an old stored value that the policy would not take today (IN10)
          if (next.length < shown.length) {
            accept(next);
            return;
          }
          // autofill and any edit the events above did not see: the same whole-value judgement
          const verdict = judge(policyRef.current, next, { paste: next.length - shown.length > 1 });
          if (verdict.ok) accept(next);
          else setReason(verdict.reason);
        }}
      />
      {/* the live region stays in the tree (empty when there is nothing to say), so a screen reader hears the reason */}
      <div className="wms-guard-reason" role="status" aria-live="polite" data-testid="input-guard-reason">
        {reason}
      </div>
    </>
  );
}
